import React, { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { GifList, IUserList } from "./GifList";
import { useSearchGifsQuery, useBoostedGifs } from "../../queries";
import { useQuery } from "hooks/useQuery";
import { ViewRegion } from "utils/metrics";

import "./TagGifList.scss";
import { IGif, INicheMap } from "services/types";
import { filterGifsList } from "utils/filterGifsList";
import { filterNichesList } from "utils/filterNichesList";
import { filterUsersList } from "utils/filterUsersList";
import { routes } from "routes";
import { mixWithBoosted } from "utils/boost";
import { debugLoadingGifs } from "utils/bugSnagHelper";

interface IProps {
  tag: string;
  tagName: string;
}

const TagGifList = ({ tag, tagName }: IProps) => {
  const query = useQuery();

  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>(1);
  const [totalGifs, setTotalGifs] = useState<number>(0);
  const [gifs, setGifs] = useState<IGif[]>([]);
  const [users, setUsers] = useState<IUserList>({});
  const [topic, setTopic] = useState<string>("a");
  const [order, setOrder] = useState(query.get("order") || "trending");
  const [tiles, setTiles] = useState<boolean>(true);
  const [verified, setVerified] = useState<boolean>(!!query.get("verified") || false);
  const [niches, setNiches] = useState<INicheMap>({});
  const navigate = useNavigate();

  const { data, isSuccess, isError, isLoading, error } = useSearchGifsQuery({
    tag,
    count: 40,
    type: topic === "a" ? undefined : topic,
    order,
    page,
    verified,
  });

  const { data: boostedPostData } = useBoostedGifs();

  const reset = useCallback(() => {
    setTotalGifs(0);
    setGifs(prev => prev.length === 0 ? prev : []);
    setPage(1);
    window.scrollTo(0, 0);
  }, []);

  const changeTopic = useCallback((value: string) => {
    console.debug(`[TagGifList] topic changed to ${value}`);
    setTopic(prev => {
      if (prev !== value) {
        reset();
      }
      return value;
    });
  }, [reset]);

  const changeOrder = useCallback((value: string) => {
    console.debug(`[TagGifList] order changed to ${value}`);
    const verifiedParam = !!query.get("verified") || verified;

    navigate(routes.multipleTag(tag.split(","), value, verifiedParam));
    setOrder(prev => {
      if (prev !== value) {
        reset();
      }
      return value;
    });
  }, [reset, navigate, query, tag, verified]);

  useEffect(() => {
    reset();
  }, [tag, reset]);

  useEffect(() => {
    if (isSuccess && data !== undefined) {
      setGifs(prev => {
        const filteredGifs = filterGifsList(prev, data.gifs);
        const gifs = mixWithBoosted({
          gifs: filteredGifs,
          boostedPostData: boostedPostData?.gifs || [],
          tiles
        });

        return gifs;
      });
      setPages(data.pages);
      setTotalGifs(data.total);
      setUsers(prev => filterUsersList(prev, [...data.users, ...(boostedPostData?.users || [])]));

      if (data.niches) {
        setNiches(prev => filterNichesList(prev, data.niches));
      }
    }
  }, [data, isSuccess, boostedPostData, tiles]);

  // TODO: debug 'error loading gif'
  useEffect(() => {
    if (isError) {
      debugLoadingGifs(error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const changeMode = useCallback((mode: boolean) => {
    console.debug(`[TagGifList] tiles changed to ${mode}`);
    setTiles(mode);
  }, []);

  const fetchMore = useCallback(() => {
    setPage(prev => prev + 1);
  }, []);

  const changeVerified = useCallback((value: boolean) => {
    const orderParam = query.get("order") || order;
    navigate(routes.multipleTag(tag.split(","), orderParam, value));
    setVerified(prev => {
      if (prev !== value) {
        reset();
      }
      return value;
    });
  }, [reset, order, navigate, query, tag]);

  return (
    <div className="tagGifList">
      <h1 className="header">🔥️ {order.charAt(0).toUpperCase() + order.slice(1)} <b>{tagName}</b> Porn GIFs and Pics</h1>

      <GifList
        topic={topic}
        order={order}
        ordering={true}
        orderingTop={true}
        tiles={tiles}
        verified={verified}
        gifs={gifs}
        users={users}
        niches={niches}
        totalLength={totalGifs}
        hasMore={page < pages}
        changeTopic={changeTopic}
        changeOrder={changeOrder}
        changeMode={changeMode}
        changeVerified={changeVerified}
        fetchMore={fetchMore}
        isError={isError}
        isLoading={isLoading}
        source={ViewRegion.Search}
        rel={`tag:${tag},${topic}`}
      />
    </div>
  );
};

export const TagGifListMemo = memo(TagGifList);

